import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  IconButton,
  Collapse,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  MapPin as LocationIcon,
  ChevronDown as ExpandMoreIcon,
  ChevronUp as ExpandLessIcon,
  Check as CheckIcon,
  Navigation as NavigationIcon,
} from 'lucide-react';
import { designSystem } from '../theme';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Radius options in miles
const RADIUS_OPTIONS = [5, 10, 15, 25, 50, 75, 100, 150, 500, 1000, 2000];

const milesToMeters = (miles) => miles * 1609.34;

// Extract a clean city/state name from a Nominatim address object.
// NEVER use county, township, or village — job boards don't recognise them.
function extractLocationName(address, fallback = 'USA') {
  if (address.city)  return address.state ? `${address.city}, ${address.state}`  : address.city;
  if (address.town)  return address.state ? `${address.town}, ${address.state}`  : address.town;
  if (address.state) return address.state;
  return address.country || fallback;
}

function MapClickHandler({ onLocationSelect }) {
  const debounceTimer = React.useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      // Immediate feedback while the reverse-geocode is in flight
      onLocationSelect({ lat, lng, name: 'Loading location...' });

      debounceTimer.current = setTimeout(async () => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=12&addressdetails=1`,
            { headers: { 'User-Agent': 'JobHound/1.0' } }
          );
          const data = await response.json();
          onLocationSelect({ lat, lng, name: extractLocationName(data.address) });
        } catch {
          onLocationSelect({ lat, lng, name: 'USA' });
        }
      }, 500);
    },
  });
  return null;
}

function MapRecenter({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView([position.lat, position.lng], map.getZoom());
  }, [map, position]);
  return null;
}

function LocationPicker({ onLocationChange, initialLocation = 'new york', initialRadius = 25, autoLocateOnMount = false }) {
  const theme = useTheme();
  const [position, setPosition] = useState(null);
  const [radius, setRadius] = useState(initialRadius);
  const [inputValue, setInputValue] = useState(initialLocation);
  const [locationName, setLocationName] = useState(initialLocation);
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const isSelectingRef = React.useRef(false);
  const userTypingRef = React.useRef(false);
  const pendingSubmitRef = React.useRef(false);
  const autoLocateAttemptedRef = React.useRef(false);

  useEffect(() => {
    geocodeLocation(initialLocation);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRadius(initialRadius);
    if (
      initialLocation &&
      initialLocation !== locationName &&
      !userTypingRef.current &&
      !pendingSubmitRef.current
    ) {
      setInputValue(initialLocation);
      setLocationName(initialLocation);
      geocodeLocation(initialLocation);
    }
  }, [initialLocation, initialRadius]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (position && locationName) {
      onLocationChange({
        location: locationName,
        lat: position.lat,
        lng: position.lng,
        radius,
      });
    }
  }, [position, radius, locationName, onLocationChange]);

  const geocodeLocation = async (query) => {
    if (!query.trim()) return;
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}, USA&limit=1&addressdetails=1`,
        { headers: { 'User-Agent': 'JobHound/1.0' } }
      );
      const data = await response.json();
      if (data.length > 0) {
        const { lat, lon, address } = data[0];
        const name = extractLocationName(address, query.trim() || 'USA');
        if (userTypingRef.current) {
          // User started typing before geocode resolved — set coords silently, don't overwrite their input
          setPosition({ lat: parseFloat(lat), lng: parseFloat(lon) });
          setLocationName(name);
        } else {
          updateLocation({ lat: parseFloat(lat), lng: parseFloat(lon), name });
        }
      }
    } catch (err) {
      console.error('Geocoding failed:', err);
      pendingSubmitRef.current = false;
    } finally {
      setIsSearching(false);
    }
  };

  const searchSuggestions = useCallback(async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}, USA&limit=5&addressdetails=1`,
        { headers: { 'User-Agent': 'JobHound/1.0' } }
      );
      const data = await response.json();
      const validSuggestions = data
        .map(item => {
          const name = extractLocationName(item.address, null);
          if (!name) return null;
          return { name, lat: parseFloat(item.lat), lng: parseFloat(item.lon) };
        })
        .filter(Boolean);
      setSuggestions(validSuggestions);
    } catch (err) {
      console.error('Autocomplete failed:', err);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue && inputValue !== locationName) searchSuggestions(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, locationName, searchSuggestions]);

  const updateLocation = ({ lat, lng, name }) => {
    userTypingRef.current = false;
    pendingSubmitRef.current = false;
    setPosition({ lat, lng });
    setLocationName(name);
    setInputValue(name);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleLocationSelect = ({ lat, lng, name }) => updateLocation({ lat, lng, name });

  const handleSuggestionClick = (suggestion) => {
    updateLocation({ lat: suggestion.lat, lng: suggestion.lng, name: suggestion.name });
    setTimeout(() => { isSelectingRef.current = false; }, 300);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (inputValue.trim()) {
      userTypingRef.current = false;
      pendingSubmitRef.current = true;
      geocodeLocation(inputValue);
    }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGpsError('Your browser does not support location access. Type your city above.');
      return;
    }
    setIsSearching(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=12&addressdetails=1`,
            { headers: { 'User-Agent': 'JobHound/1.0' } }
          );
          const data = await response.json();
          updateLocation({ lat, lng, name: extractLocationName(data.address) });
        } catch {
          updateLocation({ lat, lng, name: 'USA' });
        } finally {
          setIsSearching(false);
        }
      },
      (err) => {
        setIsSearching(false);
        if (err.code === 1) {
          setGpsError('Location access denied. Type your city above.');
        } else if (err.code === 2) {
          setGpsError('Location unavailable. Type your city above.');
        } else {
          setGpsError('Could not get location. Type your city above.');
        }
        setTimeout(() => setGpsError(null), 5000);
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    if (!autoLocateOnMount || autoLocateAttemptedRef.current) return;
    autoLocateAttemptedRef.current = true;
    handleGetCurrentLocation();
  }, [autoLocateOnMount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card variant="light" sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {/* Location Search */}
          <Box flexGrow={1} position="relative">
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type city or ZIP, press Enter, or click map..."
                value={inputValue}
                onChange={(e) => {
                  userTypingRef.current = true;
                  setInputValue(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => {
                  if (inputValue !== locationName && suggestions.length > 0) setShowSuggestions(true);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (pendingSubmitRef.current) return;
                    setShowSuggestions(false);
                    if (!isSelectingRef.current && inputValue !== locationName) setInputValue(locationName);
                  }, 250);
                }}
                InputProps={{
                  startAdornment: <LocationIcon size={20} style={{ marginRight: 8, color: '#757575' }} />,
                  endAdornment: isSearching
                    ? <CircularProgress size={20} />
                    : (
                      <IconButton
                        size="small"
                        onClick={handleGetCurrentLocation}
                        title="Use my current location"
                        sx={{ color: 'primary.main', p: 0.5 }}
                      >
                        <NavigationIcon size={18} />
                      </IconButton>
                    ),
                }}
              />
            </form>

            {showSuggestions && suggestions.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 0.5,
                }}
                elevation={3}
              >
                <List>
                  {suggestions.map((suggestion, index) => (
                    <ListItemButton
                      key={index}
                      onMouseDown={() => { isSelectingRef.current = true; }}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <ListItemText primary={suggestion.name} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Radius Selector */}
          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Within</InputLabel>
            <Select
              value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value))}
              label="Within"
            >
              {RADIUS_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>{option} miles</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Map Toggle */}
          <Button
            variant="outlined"
            size="medium"
            onClick={() => setMapExpanded(!mapExpanded)}
            endIcon={mapExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={designSystem.button.outlined}
          >
            {mapExpanded ? 'Hide Map' : 'Show Map'}
          </Button>

          {/* Confirm location */}
          <Button
            variant="contained"
            size="medium"
            onClick={handleSearchSubmit}
            startIcon={<CheckIcon />}
            sx={{
              bgcolor: 'success.main',
              color: 'white',
              fontWeight: 600,
              '&:hover': { bgcolor: 'success.dark' },
            }}
          >
            Set Location
          </Button>
        </Box>

        {gpsError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {gpsError}
          </Typography>
        )}

        <Collapse in={mapExpanded}>
          <Box mt={3}>
            <Box
              sx={{
                height: 400,
                borderRadius: 2,
                overflow: 'hidden',
                '& .leaflet-container': { height: '100%', borderRadius: 2 },
              }}
            >
              <MapContainer
                center={position ? [position.lat, position.lng] : [40.7128, -74.0060]}
                zoom={9}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler onLocationSelect={handleLocationSelect} />
                {position && (
                  <>
                    <MapRecenter position={position} />
                    <Marker position={[position.lat, position.lng]} />
                    <Circle
                      center={[position.lat, position.lng]}
                      radius={milesToMeters(radius)}
                      pathOptions={{
                        color: theme.palette.primary.main,
                        fillColor: theme.palette.primary.main,
                        fillOpacity: 0.1,
                      }}
                    />
                  </>
                )}
              </MapContainer>
            </Box>
            <Box mt={1} textAlign="center">
              <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                Click anywhere on the map to set location • Current: <strong>{locationName}</strong>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default LocationPicker;
